import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLexware } from '../../context/LexwareContext';
import { differenceInCalendarDays, startOfYear, getDaysInMonth } from 'date-fns';

const { FiTrendingUp, FiTrendingDown, FiUsers, FiFileText, FiCalendar, FiActivity, FiPieChart } = FiIcons;

function StatsCards() {
  const { data } = useLexware();
  const sales = data.sales || [];

  const statsData = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    // Berechnung der verstrichenen Kalendertage für "Aktuell"
    const daysPassedMonth = currentDay;
    const daysPassedYear = differenceInCalendarDays(today, startOfYear(today)) + 1;

    // Tage im Vorjahresmonat für korrekten Ø Vergleich (z.B. 31 Tage statt nur bis zum 20.)
    const daysInLastYearMonth = getDaysInMonth(new Date(currentYear - 1, currentMonth));
    // Tage im Vorjahr bis Ende des aktuellen Monats (für YTD Vergleich)
    const daysInLastYearYTD = differenceInCalendarDays(
      new Date(currentYear - 1, currentMonth, daysInLastYearMonth),
      new Date(currentYear - 1, 0, 1)
    ) + 1;

    // Init Variables
    let curM_Rev = 0;
    let lastM_Rev = 0; 
    let curY_Rev = 0;
    let lastY_Rev = 0; 

    let curM_InvCount = 0;
    let lastM_InvCount = 0;
    let curY_InvCount = 0;
    let lastY_InvCount = 0;

    // Customer Sets to track unique active customers
    const curM_CustIds = new Set();
    const lastM_CustIds = new Set();
    const curY_CustIds = new Set();
    const lastY_CustIds = new Set();

    sales.forEach(sale => {
      const date = new Date(sale['RG-Datum']);
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      const amount = sale._netto || 0;
      const custId = sale['Kundenummer'];

      // --- Current Month (Aktuell bis Heute) ---
      if (year === currentYear && month === currentMonth) {
        curM_Rev += amount;
        curM_InvCount++;
        if (custId) curM_CustIds.add(custId);
      }

      // --- Last Month (Vorjahr: VOLLER MONAT) ---
      // Vergleich mit dem kompletten Monat des Vorjahres
      if (year === currentYear - 1 && month === currentMonth) {
        lastM_Rev += amount;
        lastM_InvCount++;
        if (custId) lastM_CustIds.add(custId);
      }

      // --- Current Year (YTD bis Heute) ---
      if (year === currentYear) {
        curY_Rev += amount;
        curY_InvCount++;
        if (custId) curY_CustIds.add(custId);
      }

      // --- Last Year (YTD bis Ende des aktuellen Monats im Vorjahr) ---
      if (year === currentYear - 1) {
        // Nimmt alle Monate davor + den vollen aktuellen Monat des Vorjahres
        if (month <= currentMonth) {
          lastY_Rev += amount;
          lastY_InvCount++;
          if (custId) lastY_CustIds.add(custId);
        }
      }
    });

    // --- Helper Calculations ---
    const getPerc = (curr, prev) => prev !== 0 ? ((curr - prev) / prev) * 100 : 0;

    // 1. Revenue Stats
    const calcRevenueStats = (rev, prevRev, currentDays, prevDays) => {
      return {
        current: rev,
        previous: prevRev,
        diffAbs: rev - prevRev,
        diffPerc: getPerc(rev, prevRev),
        days: currentDays,
        prevDays: prevDays, 
        avg: currentDays > 0 ? rev / currentDays : 0,
        pAvg: prevDays > 0 ? prevRev / prevDays : 0
      };
    };

    // 2. Dual Metrics Stats
    const calcDualMetrics = (rev, count, prevRev, prevCount) => {
      const avg = count > 0 ? rev / count : 0;
      const prevAvg = prevCount > 0 ? prevRev / prevCount : 0;
      return {
        count: count,
        prevCount: prevCount,
        countDiffPerc: getPerc(count, prevCount),
        avg: avg,
        prevAvg: prevAvg,
        avgDiffPerc: getPerc(avg, prevAvg)
      };
    };

    return {
      revenue: {
        month: calcRevenueStats(curM_Rev, lastM_Rev, daysPassedMonth, daysInLastYearMonth),
        year: calcRevenueStats(curY_Rev, lastY_Rev, daysPassedYear, daysInLastYearYTD)
      },
      invoices: {
        month: calcDualMetrics(curM_Rev, curM_InvCount, lastM_Rev, lastM_InvCount),
        year: calcDualMetrics(curY_Rev, curY_InvCount, lastY_Rev, lastY_InvCount)
      },
      customers: {
        month: calcDualMetrics(curM_Rev, curM_CustIds.size, lastM_Rev, lastM_CustIds.size),
        year: calcDualMetrics(curY_Rev, curY_CustIds.size, lastY_Rev, lastY_CustIds.size)
      }
    };
  }, [sales]);

  const formatCurrency = (val) => `€${(val || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatPerc = (val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}%`;

  const cards = [
    { title: 'Monatsumsatz (Netto)', stats: statsData.revenue.month, icon: FiCalendar, color: 'blue', type: 'revenue' },
    { title: 'Jahresumsatz (Netto)', stats: statsData.revenue.year, icon: FiActivity, color: 'green', type: 'revenue' },
    { title: 'Rechnungs-Statistik', stats: statsData.invoices, icon: FiPieChart, color: 'orange', type: 'dual-metrics', labels: { unit: 'Rechn.', avgUnit: 'Ø' } },
    { title: 'Kunden-Analyse', stats: statsData.customers, icon: FiUsers, color: 'purple', type: 'dual-metrics', labels: { unit: 'Aktive', avgUnit: 'Ø Umsatz' } }
  ];

  const themeClasses = {
    blue: { icon: 'bg-blue-600', text: 'text-blue-600', bgSoft: 'bg-blue-50', border: 'border-blue-100', borderStrong: 'border-blue-200' },
    green: { icon: 'bg-emerald-600', text: 'text-emerald-600', bgSoft: 'bg-emerald-50', border: 'border-emerald-100', borderStrong: 'border-emerald-200' },
    orange: { icon: 'bg-orange-600', text: 'text-orange-600', bgSoft: 'bg-orange-50', border: 'border-orange-100', borderStrong: 'border-orange-200/50' },
    purple: { icon: 'bg-violet-600', text: 'text-violet-600', bgSoft: 'bg-violet-50', border: 'border-violet-100', borderStrong: 'border-violet-200/50' },
    red: { icon: 'bg-rose-600', text: 'text-rose-600', bgSoft: 'bg-rose-50', border: 'border-rose-100', borderStrong: 'border-rose-200' },
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map((card, index) => {
        const theme = themeClasses[card.color];
        return (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-all flex flex-col"
          >
            {/* Top Section */}
            <div className="flex justify-between items-start mb-4">
              <div className={`p-2.5 rounded-xl ${theme.icon} bg-opacity-10 text-current`}>
                <SafeIcon icon={card.icon} className={`text-xl ${theme.text}`} />
              </div>
              {/* Percentage Badge for Revenue Cards */}
              {card.type === 'revenue' && (
                <div className={`flex items-center gap-1 text-xs font-black px-2 py-1 rounded-full ${card.stats.diffPerc >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                  <SafeIcon icon={card.stats.diffPerc >= 0 ? FiTrendingUp : FiTrendingDown} />
                  {formatPerc(card.stats.diffPerc)}
                </div>
              )}
            </div>

            {/* DUAL METRICS LAYOUT (Invoices & Customers) */}
            {card.type === 'dual-metrics' ? (
              <div className="flex-1 flex flex-col justify-between">
                <div className="mb-2">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">{card.title}</p>
                  {/* Loop for Month and Year blocks */}
                  {['month', 'year'].map((period) => {
                    const data = card.stats[period];
                    const isMonth = period === 'month';
                    // Use theme color for Month block, gray for Year block to differentiate
                    const blockBg = isMonth ? theme.bgSoft : 'bg-gray-50';
                    const blockBorder = isMonth ? theme.border : 'border-gray-100';
                    const borderTop = isMonth ? theme.borderStrong : 'border-gray-200';

                    return (
                      <div key={period} className="mb-4 last:mb-0">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-bold text-gray-800">{isMonth ? 'Monat' : 'Jahr'}</span>
                        </div>
                        <div className={`${blockBg} rounded-lg p-2 border ${blockBorder}`}>
                          <div className="flex justify-between items-baseline mb-1">
                            {/* Count Metric */}
                            <div className="flex items-baseline gap-1">
                              <span className="text-xs font-bold text-gray-900">{data.count}</span>
                              <span className={`text-[9px] font-bold ${data.countDiffPerc >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {formatPerc(data.countDiffPerc)}
                              </span>
                              <span className="text-[10px] font-medium text-gray-500">{card.labels.unit}</span>
                            </div>
                            {/* Avg Metric */}
                            <div className="flex items-baseline gap-1">
                              <span className="text-xs font-bold text-gray-900">{formatCurrency(data.avg)}</span>
                              <span className={`text-[9px] font-bold ${data.avgDiffPerc >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {formatPerc(data.avgDiffPerc)}
                              </span>
                              <span className="text-[10px] font-medium text-gray-500">{card.labels.avgUnit}</span>
                            </div>
                          </div>
                          <div className={`flex justify-between items-baseline pt-1 border-t ${borderTop} text-[10px] text-gray-500`}>
                            <span>VJ: {data.prevCount}</span>
                            <span>Ø {formatCurrency(data.prevAvg)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                {/* REVENUE LAYOUT */}
                <div className="mb-5">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1">{card.title}</p>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h3 className="text-2xl font-black text-gray-900 tracking-tight">
                      {formatCurrency(card.stats.current)}
                    </h3>
                    <span className={`text-xs font-bold ${card.stats.diffAbs >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {card.stats.diffAbs >= 0 ? '+' : ''}{formatCurrency(card.stats.diffAbs)}
                    </span>
                  </div>
                  <div className="text-[10px] font-semibold text-gray-400 mt-1">
                    Vorjahr: {formatCurrency(card.stats.previous)}
                  </div>
                </div>

                <div className="space-y-2 mt-auto">
                  {/* Row Aktuell */}
                  <div className="flex items-center justify-between p-2 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="flex flex-col">
                      <span className="text-[9px] text-gray-400 font-black uppercase">Aktuell</span>
                      <span className="text-xs font-bold text-gray-700">{card.stats.days} Tage</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[9px] text-gray-400 font-black uppercase block">Ø / Tag</span>
                      <span className="text-xs font-black text-blue-600">{formatCurrency(card.stats.avg)}</span>
                    </div>
                  </div>

                  {/* Row Vorjahr */}
                  <div className="flex items-center justify-between p-2 rounded-xl bg-white border border-gray-100">
                    <div className="flex flex-col">
                      <span className="text-[9px] text-gray-400 font-black uppercase">Vorjahr</span>
                      <span className="text-xs font-medium text-gray-500">{card.stats.prevDays} Tage</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[9px] text-gray-400 font-black uppercase block">Ø / Tag</span>
                      <span className="text-xs font-bold text-gray-500">{formatCurrency(card.stats.pAvg)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

export default StatsCards;